import {
  Card,
  CryptoIcon,
  ExternalApiBadge,
  FlaggedBadge,
  getNetworkName,
  StatusBadge,
  SwapArrow,
  SwapArrowMobile,
  TransactionTypeBadge,
} from '@valiant/ui-shared'
import { useNavigate } from 'react-router-dom'
import type { GetTransactionsQuery } from '../../../generated/graphql'
import { maskSwapStatus } from '../../../utils/swapStatus'

/** Node shape from `getTransactions` — keeps `isExternalApi` selection type-load-bearing. */
export type TransactionListItem = GetTransactionsQuery['getTransactions']['nodes'][number]

interface TransactionItemProps {
  transaction: TransactionListItem
  variant?: 'pending' | 'completed'
}

// Helper function to format date
const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  if (date.getTime() === 0) {
    return null
  }
  const now = new Date()
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))

  if (diffInMinutes < 60) {
    return {
      date: `${diffInMinutes} min ago`,
      hours: null,
    }
  } else if (diffInMinutes < 1440) {
    const hours = Math.floor(diffInMinutes / 60)
    return {
      date: `${hours} hour${hours > 1 ? 's' : ''} ago`,
      hours: null,
    }
  } else {
    return {
      date: date.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      }),
      hours: date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      }),
    }
  }
}

function CoinComponent({
  currency,
  currencyImage,
  network,
  amount,
}: {
  currency: string
  currencyImage: string
  network: string
  amount?: string
}) {
  return (
    <div className="flex flex-col items-center gap-1 md:gap-2 shrink-0">
      <div className="flex items-center justify-center gap-2">
        <CryptoIcon
          symbol={currency || ''}
          imageOverride={currencyImage || ''}
          className="w-8 h-8"
        />
        <div className="md:flex items-center gap-2">
          <div className="hidden md:block">
            {amount && (
              <div>
                <div className="font-medium text-sm">{amount}</div>
              </div>
            )}
          </div>
          <div>
            <div className="text-xs">{currency}</div>
            <div className="text-xs text-card-text">
              {getNetworkName(network || '', currency || '')}
            </div>
          </div>
        </div>
      </div>
      {amount && (
        <div className="md:hidden">
          <div className="font-bold text-sm">{amount}</div>
        </div>
      )}
    </div>
  )
}

export function TransactionItem({ transaction, variant = 'completed' }: TransactionItemProps) {
  const isTrade = transaction.type === 'TRADE'
  const navigate = useNavigate()

  const handleViewDetails = (transactionId: string) => {
    if (transaction.type === 'DEPOSIT') {
      navigate(`/transactions/deposit/${transactionId}`)
    } else if (transaction.type === 'TRADE') {
      navigate(`/transactions/swap/${transactionId}`)
    }
  }

  const formattedDate = formatDate(
    transaction.finalizedDate || transaction.approvedDate || transaction.createdDate || '',
  )

  return (
    <Card
      className="p-4 cursor-pointer hover:bg-background/5"
      onClick={() => handleViewDetails(transaction.id)}
    >
      <div
        className={`flex items-start md:items-center justify-between w-full ${variant === 'completed' ? 'grow-1' : ''}`}
      >
        <div
          className={`flex items-center justify-between md:justify-start gap-4 flex-wrap w-full ${variant === 'completed' ? 'grow-1' : ''}`}
        >
          {/* Activity Type Badge */}
          <TransactionTypeBadge type={isTrade ? 'TRADE' : 'DEPOSIT'} />

          {/* Transaction Details */}
          <div className="flex items-start gap-3 w-full md:w-auto">
            {isTrade ? (
              <>
                {/* From Currency */}
                <CoinComponent
                  currency={transaction.transaction.fromCurrency || ''}
                  currencyImage={transaction.transaction.fromCurrencyImage || ''}
                  network={transaction.transaction.fromNetwork || ''}
                  amount={transaction.transaction.from || ''}
                />

                {/* Arrow */}
                <div className="hidden md:flex items-center self-center">
                  <SwapArrow size="md" />
                </div>

                <div className="block md:hidden shrink-0 self-stretch">
                  <SwapArrowMobile />
                </div>

                {/* To Currency */}
                <CoinComponent
                  currency={transaction.transaction.toCurrency || ''}
                  currencyImage={transaction.transaction.toCurrencyImage || ''}
                  network={transaction.transaction.toNetwork || ''}
                  amount={transaction.transaction.to || ''}
                />
              </>
            ) : (
              /* Deposit */
              <CoinComponent
                currency={transaction.transaction.currency || ''}
                currencyImage={transaction.transaction.fromCurrencyImage || ''}
                network={transaction.transaction.fromNetwork || ''}
                amount={transaction.transaction.amount || ''}
              />
            )}
          </div>
        </div>

        {/* Status, Date */}
        <div
          className={`flex ${variant === 'completed' ? 'grow-1' : ''} items-end gap-8 flex-col w-full md:items-center md:flex-row md:w-auto ${variant === 'completed' ? '' : 'justify-end md:justify-start'} md:shrink-0`}
        >
          <div className="flex items-center gap-2">
            <StatusBadge
              status={
                transaction.depositStatus ||
                maskSwapStatus(transaction.swapStatus, transaction.isExternalApi) ||
                transaction.status
              }
              type={transaction.type}
            />
            <FlaggedBadge flagged={transaction.isFlagged} mobileStyling={true} />
            <ExternalApiBadge isExternalApi={transaction.isExternalApi} />
          </div>
          <div
            className={`text-sm text-right shrink-0 ${variant === 'pending' ? 'flex items-center gap-2 text-xs font-semibold' : ''}`}
          >
            {formattedDate && (
              <>
                {variant === 'completed' ? (
                  <>
                    <div className="text-xs font-semibold">{formattedDate.date}</div>
                    <div className="text-xs text-gray-500">
                      {formattedDate.hours && formattedDate.hours}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="min-w-[80px]">{formattedDate.date}</div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Card>
  )
}
